import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { STALE_TIME_DEFAULT } from './constants';
import { AuthProvider, useAuthContext } from './context/AuthContext';
import { TimeFormatProvider } from './context/TimeFormatContext';
import { StoreManagementProvider } from './context/StoreManagementContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { TourProvider } from './context/TourContext';
import { Layout } from './components/Layout';
import { LoadingScreen } from './components/LoadingScreen';
import { OnboardingModal, FeatureTour } from './components/Onboarding';

// Create theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#662C8B',
    },
    secondary: {
      main: '#DE1D3B',
    },
    warning: {
      main: '#FAA51F',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
});

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME_DEFAULT,
      retry: (failureCount, error) => {
        // Don't retry on 401 - session has expired, let the auth handler deal with it
        const axiosError = error as { response?: { status?: number } };
        if (axiosError?.response?.status === 401) {
          return false;
        }
        // Default: retry once
        return failureCount < 1;
      },
    },
  },
});

// Authenticated App Component
function AuthenticatedApp() {
  const { isLoading, isAuthenticated } = useAuthContext();

  if (isLoading) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Authentication Required</h1>
          <p className="text-gray-600">Redirecting to MApp authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <OnboardingModal />
      <FeatureTour />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Navigate to="/store-management" replace />} />
          <Route path="store-management" element={<div />} />
          <Route path="store/:storeId" element={<div />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

// Main App Component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <BrowserRouter>
            <AuthProvider>
              <TimeFormatProvider>
                <OnboardingProvider>
                  <TourProvider>
                    <StoreManagementProvider>
                      <AuthenticatedApp />
                    </StoreManagementProvider>
                  </TourProvider>
                </OnboardingProvider>
              </TimeFormatProvider>
            </AuthProvider>
          </BrowserRouter>
          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
